import React, { useState, useEffect } from 'react';
import hoistStatics from 'hoist-non-react-statics';
import queryString from 'query-string';

import {
    IntlProvider,
    Locales,
    ModalProvider,
    getLanguageCode, 
    usePageSettings,
} from '@ratehub/base-ui';

import getLocaleMessages from './getLocaleMessages';


/**
 * @public
 * Wrap a React component with an <IntlProvider>
 * @param {React.Component} Component
 * @returns {React.Component}
 */
function withDynamicLocale(Component) {
    function WrappedComponent(props) {
        const settings = usePageSettings();

        const [ locale, setLocale ] = useState(settings.locale || Locales.ENGLISH);

        const messages = getLocaleMessages(locale);

        useEffect(() => {
            // SPECIAL CASE: if language is EXPLICITLY specified, don't change it!
            if (settings.locale) {
                return;
            }

            // Attempt to read it from a query param
            const { lang } = queryString.parse(window.location.search, {
                // ignore duplicate query lang params to prevent lang becoming an array
                arrayFormat: 'bracket',
            });
            if (lang) {
                setLocale(`${lang}-CA`);
                return;
            }

            // Attempt to read it from the cookie.
            const langCode = getLanguageCode();
            if (langCode) {
                setLocale(`${langCode}-CA`);
            }
        }, []);

        return (
            <IntlProvider
                locale={locale}
                messages={messages}
            >
                <ModalProvider>
                    <Component
                        {...props}
                    />
                </ModalProvider>
            </IntlProvider>
        );
    }

    // Hoisting statics is critical to allow Next.js to work with wrapped pages
    // (getInitialProps, getStaticProps, getStaticPaths)
    hoistStatics(WrappedComponent, Component);

    return WrappedComponent;
}

export default withDynamicLocale;
